import React, { useEffect, useState } from "react";
import { DollarSign, PiggyBank, TrendingUp, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";

export default function FinancialWellness() {
  const [s, setS] = useState(null);
  const [contract, setContract] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/financial-wellness/summary").then(r=>setS(r.data)); }, []);

  const analyze = async () => {
    if (!contract.trim()) return;
    setBusy(true);
    try {
      const data = await aiJob("/financial-wellness/contract-analyze", { contract_text: contract, specialty: "Prosthodontics", region: "California" });
      setResult({ text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Contract analyzed");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-sm text-brand-muted">Loading…</div>;

  return (
    <>
      <PageHeader eyebrow="Module 20" title="Physician Financial Wellness"
        subtitle="Practice vs personal income, retirement, taxes, contracts and partnership track." testid="fin-page" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={DollarSign} label="Practice Revenue YTD" value={`$${(s.practice_revenue_ytd/1000).toFixed(0)}k`} testid="kpi-prac" />
        <KpiCard icon={DollarSign} label="Personal Income YTD" value={`$${(s.personal_income_ytd/1000).toFixed(0)}k`} testid="kpi-pers" />
        <KpiCard icon={PiggyBank} label="Retirement Total" value={`$${(s.retirement.total/1000).toFixed(0)}k`} accent testid="kpi-ret" />
        <KpiCard icon={TrendingUp} label="Partnership Equity (est)" value={`$${(s.partnership_track.estimated_equity_value/1000).toFixed(0)}k`} hint={`Year ${s.partnership_track.current_year}/${s.partnership_track.vesting_year}`} testid="kpi-eq" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Retirement" testid="retirement">
          <div className="space-y-2 text-sm">
            <Row label="SEP IRA" v={s.retirement.sep_ira} />
            <Row label="401(k)" v={s.retirement.k401} alt={s.retirement["401k"]} />
            <Row label="Defined Benefit" v={s.retirement.defined_benefit} />
          </div>
        </SectionCard>
        <SectionCard title="Student Loan Strategy" testid="loan">
          <div className="text-2xl font-semibold text-brand-primary">${(s.student_loan_balance/1000).toFixed(1)}k</div>
          <div className="text-xs text-brand-muted">Balance</div>
          <div className="mt-3 p-3 bg-brand-bg rounded-md text-sm">{s.student_loan_optimal_strategy}</div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Tax Strategy Alerts" testid="tax-alerts">
          <ul className="space-y-2 text-sm">
            {s.tax_alerts.map((t,i)=>(<li key={i} className="flex gap-2"><span className="text-brand-teal mt-1">●</span>{t}</li>))}
          </ul>
        </SectionCard>
        <SectionCard title="Employment Contract Analyzer" testid="contract-analyzer">
          <textarea value={contract} onChange={(e)=>setContract(e.target.value)} rows={5} placeholder="Paste employment contract text…"
            className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="contract-input" />
          <button onClick={analyze} disabled={busy} className="mt-2 bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-50" data-testid="analyze-contract-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze
          </button>
          {result && (
            <div className="mt-3">
              <AIOutputDisclaimer />
              <pre className="text-xs whitespace-pre-wrap bg-brand-bg p-3 rounded-md max-h-72 overflow-y-auto" data-testid="fin-result">{result.text}</pre>
              {result.verification_footer && (
                <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="fin-verification-footer">{result.verification_footer}</div>
              )}
              <ShowReasoning reasoning={result.reasoning} score={result.score} testid="fin-reasoning" />
              <AIFeedback module="financial-wellness" outputType="contract_analysis" testid="fin-feedback" />
            </div>
          )}
        </SectionCard>
      </div>
    </>
  );
}

const Row = ({ label, v, alt }) => (
  <div className="flex justify-between border-b border-brand pb-2">
    <span className="text-brand-muted">{label}</span>
    <span className="font-mono font-semibold">${((v ?? alt) || 0).toLocaleString()}</span>
  </div>
);
